import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FileText, Download, Loader2, ShieldCheck } from "lucide-react";

export default function TransparencyPage() {
  const [docs, setDocs] = useState([]);
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const { t, tr } = useLang();

  useEffect(() => {
    api.get("/public/documents").then((r) => setDocs(r.data)).finally(() => setLoading(false));
    api.get("/public/content/transparency").then((r) => setContent(r.data || {})).catch(() => {});
  }, []);

  return (
    <div data-testid="public-transparency-page">
      <section className="bg-[#0E2916] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C59B27] font-mono">{t("Trust & Accountability")}</p>
          <h1 className="font-display text-4xl lg:text-5xl font-semibold mt-2">{t("Transparency")}</h1>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="flex items-start gap-4 mb-10">
          <ShieldCheck className="h-8 w-8 text-[#C59B27] shrink-0" />
          <p className="text-[#314337] leading-relaxed">
            {tr(content, "intro") || "We are committed to full transparency. Official registration details, tax approvals, annual and financial reports and project documents are published here as they become available."}
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#C59B27]" /></div>
        ) : docs.length === 0 ? (
          <div className="bg-[#F2EEE5] rounded-xl p-10 text-center text-[#5C7264]">
            <FileText className="h-10 w-10 mx-auto mb-3 text-[#CBD2CC]" />
            {t("Official documents will be published here soon.")}
          </div>
        ) : (
          <div className="space-y-3">
            {docs.map((d) => (
              <div key={d.id} className="flex items-center gap-4 bg-white rounded-xl border border-[#E2DAD0] p-5" data-testid={`document-${d.id}`}>
                <div className="h-11 w-11 rounded-lg bg-[#E8F0EA] flex items-center justify-center shrink-0"><FileText className="h-5 w-5 text-[#194324]" /></div>
                <div className="flex-1">
                  <div className="flex items-center gap-2"><Badge className="bg-[#F9F3E3] text-[#A8811A] border-0 text-xs">{d.category}</Badge></div>
                  <div className="font-medium text-[#194324] mt-1">{d.title}</div>
                  {d.description && <p className="text-sm text-[#5C7264]">{d.description}</p>}
                </div>
                {d.file_path && (
                  <a href={fileUrl(d.file_path)} target="_blank" rel="noreferrer">
                    <Button variant="outline" className="rounded-full"><Download className="h-4 w-4 mr-2" /> {t("View")}</Button>
                  </a>
                )}
              </div>
            ))}
          </div>
        )}

        {content.note && <p className="mt-8 text-sm text-[#5C7264] italic">{tr(content, "note")}</p>}
      </section>
    </div>
  );
}
